import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIdentity } from '../contexts/IdentityContext';
import { gemsApi } from '../lib/api';
import { X, Gem, BookOpen, Lightbulb, Type, ImagePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export const CreateGemDialog = ({ open, onClose, onCreated }) => {
  const { t, language } = useLanguage();
  const { anonId } = useIdentity();
  const [verseRef, setVerseRef] = useState('');
  const [gemText, setGemText] = useState('');
  const [insight, setInsight] = useState('');
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAddPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (media.length >= 4) return;
      if (file.size > 3000000) {
        toast.error(t('image_too_large') || 'Image must be less than 3MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setMedia(prev => [...prev.slice(0, 3), { type: 'image', data: reader.result, caption: '' }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemoveMedia = (index) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleCaptionChange = (index, caption) => {
    setMedia(prev => prev.map((m, i) => i === index ? { ...m, caption } : m));
  };

  const handleSubmit = async () => {
    if (!gemText.trim() && !insight.trim() && media.length === 0) return;
    setLoading(true);
    try {
      const res = await gemsApi.createGem({
        text: gemText.trim() || (media.length > 0 ? media[0].caption || '📷' : ''),
        verse_ref: verseRef.trim() || null,
        insight: insight.trim() || null,
        lang: language,
        author_anon_id: anonId,
        media: media.length > 0 ? media : null,
      });
      onCreated(res.data);
      setVerseRef(''); setGemText(''); setInsight(''); setMedia([]);
    } catch (err) {
      toast.error(t('error') || 'Failed to post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50" />
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-[3%] z-50 mx-auto max-w-md max-h-[94vh]">
            <div className="bg-card border border-border rounded-2xl p-5 shadow-[0_18px_50px_-30px_hsl(var(--shadow-color)/0.65)] max-h-[94vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-[var(--font-heading)] flex items-center gap-2">
                  <Gem className="w-4 h-4 text-primary" />{t('create_gem')}
                </h2>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Verse Reference */}
              <div className="mb-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <BookOpen className="w-3.5 h-3.5" />{t('verse_ref_placeholder')}
                </label>
                <input type="text" value={verseRef} onChange={(e) => setVerseRef(e.target.value)}
                  placeholder="e.g. Proverbs 2:1-5"
                  className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
                  data-testid="gem-composer-verse-input" />
              </div>

              {/* Gem Text */}
              <div className="mb-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <Type className="w-3.5 h-3.5" />{t('gem_text_label')}
                </label>
                <textarea value={gemText} onChange={(e) => setGemText(e.target.value)}
                  placeholder={t('gem_text_placeholder')} rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))] leading-6"
                  data-testid="gem-composer-textarea" />
              </div>

              {/* Insight */}
              <div className="mb-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <Lightbulb className="w-3.5 h-3.5" />{t('insight_label')}
                </label>
                <textarea value={insight} onChange={(e) => setInsight(e.target.value)}
                  placeholder={t('insight_placeholder')} rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))] leading-6"
                  data-testid="gem-composer-insight-input" />
              </div>

              {/* Media Preview */}
              {media.length > 0 && (
                <div className="mb-3 grid grid-cols-2 gap-2">
                  {media.map((m, i) => (
                    <div key={i} className="relative rounded-xl border border-border overflow-hidden bg-muted">
                      {m.type === 'image' ? (
                        <img src={m.data} alt="" className="w-full h-28 object-cover" />
                      ) : (
                        <div className="w-full h-28 flex items-center justify-center bg-black/10">
                          <Film className="w-8 h-8 text-muted-foreground" />
                          <p className="text-[9px] text-muted-foreground ml-1 truncate max-w-[80px]">{m.data}</p>
                        </div>
                      )}
                      <button onClick={() => handleRemoveMedia(i)}
                        className="absolute top-1 right-1 p-1 bg-black/50 rounded-full">
                        <X className="w-3 h-3 text-white" />
                      </button>
                      <input type="text" value={m.caption} onChange={(e) => handleCaptionChange(i, e.target.value)}
                        placeholder={t('caption') || 'Caption...'}
                        className="w-full px-2 py-1.5 text-[10px] bg-background border-t border-border focus:outline-none" />
                    </div>
                  ))}
                </div>
              )}

              {/* Add Photo Button */}
              <div className="mb-4">
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleAddPhoto} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} disabled={media.length >= 4}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs text-muted-foreground hover:bg-accent disabled:opacity-40 transition-colors"
                  data-testid="gem-add-photo">
                  <ImagePlus className="w-3.5 h-3.5" />{t('add_photo') || 'Photo'} {media.length > 0 ? `(${media.length}/4)` : ''}
                </button>
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 py-2.5 rounded-xl border border-border bg-background/70 text-sm font-medium hover:bg-background transition-colors">
                  {t('cancel')}
                </button>
                <button onClick={handleSubmit}
                  disabled={(!gemText.trim() && !insight.trim() && media.length === 0) || loading}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium shadow-[0_10px_25px_-18px_hsl(var(--shadow-color)/0.65)] hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  data-testid="gem-composer-submit-button">
                  {loading ? t('loading') : t('post')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateGemDialog;
